import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowDownRight,
  ArrowUpRight,
  Ban,
  BarChart3,
  CheckCircle2,
  Clock3,
  FileText,
  LineChart,
  List,
  Loader2,
  Percent,
  TrendingUp,
  Wallet,
  XCircle,
} from "lucide-react";

import Checkbox from "../components/ui/Checkbox";
import NumberFlowValue from "../components/ui/NumberFlowValue";
import {
  Card,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  DataTable,
  DateRangeField,
  MultiSelectField,
  PageLoader,
  SearchBar,
  Tabs,
  Tooltip,
  tabPanelProps,
} from "../components/ui";
import FiltersSheet from "../components/FiltersSheet";
import ParceiroIdentidade, { nomeExibicaoParceiro } from "../components/parceiro/ParceiroIdentidade";
import usePageFilters from "../hooks/usePageFilters";
import useBreadcrumbEntity from "../hooks/useBreadcrumbEntity";
import {
  DASHBOARD_REFRESH_REQUEST_EVENT,
  clearDashboardRefreshState,
  formatRefreshCountdown,
  publishDashboardRefreshState,
} from "../services/dashboardRefreshService";
import { buscarParceiro } from "../services/parceiroService";
import {
  buscarVidaVendedorDetalhada,
  listarBancosAtivos,
  listarConveniosAtivos,
} from "../services/joinService";
import {
  aggregateJoinRows,
  buildJoinComparisonMap,
  buildJoinPeriodoComparativo,
  calculateChange,
  filterJoinRowsBySearch,
  flattenJoinProducaoAgrupada,
  formatPeriodoDescricao,
  getJoinGranularidadeEvolucao,
  sanitizeJoinPeriodo,
  sumJoinContracts,
} from "../utils/joinVidaAgente";
import { SwalWithTheme } from "../utils/sweetalert";
import { toneClassMap } from "../utils/themeContrast";
import { kpiAccentByLabel, kpiComparisonToneClass, kpiGradientStyle, kpiInkClasses } from "../utils/kpiCard";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import PreFiltroField from "../components/PreFiltroField";
import CampanhaMetaBar, { CampanhaIcone, corDaCampanha } from "../components/CampanhaMetaBar";
import { listarCampanhasVigentes } from "../services/campanhaService";
import { formatCompactBRL } from "../utils/money";

const STORAGE_PREFIX = "parceiro-detalhes:join-detalhada:";
const TIPO_PRODUTO_LABEL = "Tipo de produto";
const TIPO_PRODUTO_ORDER = {
  NOVO: 0,
  PORT: 1,
  REFIN: 2,
};
// Cores das séries pela paleta validada (--chart-1..8), as mesmas das métricas no dashboard.
const EVOLUTION_METRIC_OPTIONS = [
  { key: "producaoBruta", label: "Bruta", description: "produção bruta", formatter: formatCurrency, color: "var(--chart-1)" },
  { key: "producaoLiquida", label: "Líquida", description: "produção líquida", formatter: formatCurrency, color: "var(--chart-2)" },
  { key: "producaoComissionada", label: "Comissionada", description: "produção comissionada", formatter: formatCurrency, color: "var(--chart-3)" },
  { key: "contratosTotal", label: "Contratos", description: "volume de contratos", formatter: formatCount, color: "var(--chart-4)" },
];

const RANKING_METRIC_OPTIONS = [
  { key: "BRUTO", label: "Bruto", metricKey: "producaoBrutaTotal", formatter: formatCurrency, barColor: "var(--chart-1)" },
  { key: "LIQUIDO", label: "Líquido", metricKey: "producaoLiquidaTotal", formatter: formatCurrency, barColor: "var(--chart-2)" },
  { key: "COMISSIONADO", label: "Comissionado", metricKey: "producaoComissionadaTotal", formatter: formatCurrency, barColor: "var(--chart-3)" },
  { key: "CONTRATOS", label: "Contratos", metricKey: "contratosTotal", formatter: formatCount, barColor: "var(--chart-4)" },
];

const VIDA_ABAS_ID = "vida-agente-abas";
const VIDA_ABAS = [
  { value: "evolucao", label: "Evolução", icon: LineChart },
  { value: "digitacao", label: "Digitação", icon: FileText },
  { value: "rankings", label: "Rankings", icon: BarChart3 },
  { value: "lista", label: "Lista detalhada", icon: List },
];

const AGRUPAMENTOS = [
  { campo: "agruparBanco", label: "Banco" },
  { campo: "agruparConvenio", label: "Convênio" },
  { campo: "agruparTipo", label: TIPO_PRODUTO_LABEL },
];

// Mesmo intervalo do dashboard e dos detalhes do parceiro, com a contagem na barra superior.
const AUTO_REFRESH_SECONDS = 30;

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

function formatSpread(value) {
  return Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCount(value) {
  return Number(value || 0).toLocaleString("pt-BR");
}

function getFirstFiniteNumber(source, aliases = [], fallback = 0) {
  for (const alias of aliases) {
    const value = Number(source?.[alias]);
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return fallback;
}

function normalizeEvolutionPoint(point = {}) {
  const contratosAprovados = getFirstFiniteNumber(point, ["contratosAprovados", "ContratosAprovados"], 0);
  const contratosNegados = getFirstFiniteNumber(point, ["contratosNegados", "ContratosNegados"], 0);
  const contratosCancelados = getFirstFiniteNumber(point, ["contratosCancelados", "ContratosCancelados"], 0);
  const contratosEmAndamento = getFirstFiniteNumber(point, ["contratosEmAndamento", "ContratosEmAndamento"], 0);

  return {
    producaoBruta: getFirstFiniteNumber(
      point,
      ["producaoBruta", "ProducaoBruta", "producaoBrutaTotal", "ProducaoBrutaTotal", "valor", "Valor"],
      0
    ),
    producaoLiquida: getFirstFiniteNumber(
      point,
      ["producaoLiquida", "ProducaoLiquida", "producaoLiquidaTotal", "ProducaoLiquidaTotal"],
      0
    ),
    producaoComissionada: getFirstFiniteNumber(
      point,
      ["producaoComissionada", "ProducaoComissionada", "producaoComissionadaTotal", "ProducaoComissionadaTotal"],
      0
    ),
    contratosTotal: getFirstFiniteNumber(
      point,
      ["contratosTotal", "ContratosTotal", "quantidade", "Quantidade", "qtd", "Qtd", "totalContratos", "TotalContratos"],
      contratosAprovados + contratosNegados + contratosCancelados + contratosEmAndamento
    ),
  };
}

function normalizeContractStatusPoint(point = {}) {
  const contratosAprovados = getFirstFiniteNumber(point, ["contratosAprovados", "ContratosAprovados", "aprovados", "Aprovados"], 0);
  const contratosCancelados = getFirstFiniteNumber(point, ["contratosCancelados", "ContratosCancelados", "cancelados", "Cancelados"], 0);
  const contratosNegados = getFirstFiniteNumber(point, ["contratosNegados", "ContratosNegados", "negados", "Negados"], 0);
  const contratosEmAndamento = getFirstFiniteNumber(point, ["contratosEmAndamento", "ContratosEmAndamento", "emAndamento", "EmAndamento", "andamento", "Andamento"], 0);
  return {
    contratosAprovados,
    contratosCancelados,
    contratosNegados,
    contratosEmAndamento,
    total: contratosAprovados + contratosCancelados + contratosNegados + contratosEmAndamento,
  };
}

function getStorageKey(parceiroId) {
  return `${STORAGE_PREFIX}${parceiroId}`;
}

// periodoUrl: período vindo de "Ver detalhada" (?de=&ate=). Tem prioridade sobre o guardado na sessão,
// para a detalhada abrir no mesmo recorte da tela de onde se veio.
function loadFiltersFromSession(parceiroId, periodoUrl = null) {
  const defaultPeriod = sanitizeJoinPeriodo();
  const fallback = {
    ...defaultPeriod,
    bancosIds: [],
    conveniosIds: [],
    tiposConvenio: [],
    searchTerm: "",
    agruparBanco: true,
    agruparConvenio: true,
    agruparTipo: false,
  };

  const periodoDaUrl = periodoUrl?.dataInicio && periodoUrl?.dataFim ? sanitizeJoinPeriodo(periodoUrl) : null;

  if (!parceiroId || typeof window === "undefined") return { ...fallback, ...periodoDaUrl };

  try {
    const raw = window.sessionStorage.getItem(getStorageKey(parceiroId));
    if (!raw) return { ...fallback, ...periodoDaUrl };
    const parsed = JSON.parse(raw);
    return {
      ...fallback,
      ...(periodoDaUrl || sanitizeJoinPeriodo({ dataInicio: parsed?.dataInicio, dataFim: parsed?.dataFim })),
      bancosIds: Array.isArray(parsed?.bancosIds) ? parsed.bancosIds.map(String) : [],
      conveniosIds: Array.isArray(parsed?.conveniosIds) ? parsed.conveniosIds.map(String) : [],
      tiposConvenio: Array.isArray(parsed?.tiposConvenio) ? parsed.tiposConvenio.map(String) : [],
      searchTerm: String(parsed?.searchTerm || ""),
      agruparBanco: parsed?.agruparBanco !== false,
      agruparConvenio: parsed?.agruparConvenio !== false,
      agruparTipo: parsed?.agruparTipo === true,
    };
  } catch {
    return { ...fallback, ...periodoDaUrl };
  }
}

function buildComparisonTitle(currentLabel, previousLabel) {
  if (!previousLabel) return undefined;
  return `Atual: ${currentLabel}\nAnterior comparado: ${previousLabel}`;
}

// Etiqueta sólida no verde e vermelho das tags. lowerIsBetter: onde subir é ruim (cancelados,
// negados), a alta sai vermelha em vez de verde.
function ComparisonPill({ change, compact = false, title, lowerIsBetter = false }) {
  if (!change) return null;

  const isUp = change.direction === "up";
  const isDown = change.direction === "down";
  const noBase = change.percent == null;
  const Icon = isDown ? ArrowDownRight : ArrowUpRight;
  const toneClass = kpiComparisonToneClass(change, { lowerIsBetter });

  const percentLabel =
    change.percent == null
      ? "sem base"
      : `${Number(change.percent).toLocaleString("pt-BR", {
        minimumFractionDigits: compact ? 0 : 1,
        maximumFractionDigits: compact ? 0 : 1,
      })}%`;

  return (
    <Tooltip content={title}>
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold shadow-sm [text-shadow:none] ${toneClass}`}
      >
        {!noBase && (isUp || isDown) ? <Icon className="h-3.5 w-3.5" /> : null}
        {percentLabel}
      </span>
    </Tooltip>
  );
}

function MetricCell({ value, previousValue, formatter, showComparison = true, lowerIsBetter = false }) {
  const currentLabel = formatter(value);
  const previousLabel = formatter(previousValue);
  const change = calculateChange(value, previousValue);
  const tooltip = buildComparisonTitle(currentLabel, previousLabel);

  const flowFormat = useMemo(() => {
    if (formatter === formatCurrency) return { style: "currency", currency: "BRL" };
    if (formatter === formatCount) return { maximumFractionDigits: 0 };
    if (formatter === formatSpread) return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    if (formatter === formatPercent) return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return null;
  }, [formatter]);

  const flowSuffix = formatter === formatPercent ? "%" : undefined;
  const flowValue = flowFormat ? (
    <NumberFlowValue className="tabular-nums" value={Number(value || 0)} format={flowFormat} suffix={flowSuffix} />
  ) : (
    currentLabel
  );

  return (
    <div className="space-y-1">
      <div className="font-medium text-foreground">{flowValue}</div>
      {showComparison ? (
        <ComparisonPill change={change} compact title={tooltip} lowerIsBetter={lowerIsBetter} />
      ) : null}
    </div>
  );
}

// Cartões em degradê, mesma cor do cartão equivalente no dashboard (utils/kpiCard).
function KpiCardGrid({ cards, className = "" }) {
  return (
    <div className={className}>
      {cards.map((card) => {
        const CardIcon = card.icon;
        const { accent, ink } = kpiAccentByLabel(card.label);
        const inkClasses = kpiInkClasses(ink);
        const hasComparison = card.showComparison !== false && card.change;
        return (
          <article
            key={card.label}
            className={`relative overflow-hidden rounded-2xl p-4 shadow-md ${inkClasses.root}`}
            style={kpiGradientStyle(accent, ink)}
          >
            <div className="min-w-0 pr-10">
              <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${inkClasses.label}`}>{card.label}</p>
              <p className="mt-1 break-all font-display text-xl font-semibold leading-tight">{card.value}</p>
            </div>
            <CardIcon className={`absolute right-4 top-4 h-5 w-5 ${inkClasses.icon}`} aria-hidden="true" />
            {hasComparison ? (
              <div className="mt-2 flex justify-end">
                <ComparisonPill change={card.change} title={card.tooltip} compact lowerIsBetter={card.lowerIsBetter} />
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

// Mesma chave do dashboard: campanha e linha se encontram pelo NOME do banco/convenio.
function chaveCampanha(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();
}

// A linha em campanha vai para o topo: o ranking corta e ela podia ficar fora da vista.
function ordenarCampanhasPrimeiro(rows, labelSelector, campanhasPorNome) {
  if (!campanhasPorNome?.size) return rows;
  const emCampanha = [];
  const demais = [];
  rows.forEach((row) => {
    (campanhasPorNome.has(chaveCampanha(labelSelector(row))) ? emCampanha : demais).push(row);
  });
  return [...emCampanha, ...demais];
}

function HorizontalRankingChart({ title, subtitle, rows = [], metricKey, formatter, labelSelector, campanhasPorNome }) {
  const flowFormat = (() => {
    if (formatter === formatCurrency) return { format: { style: "currency", currency: "BRL" } };
    if (formatter === formatCount) return { format: { maximumFractionDigits: 0 } };
    if (formatter === formatSpread) return { format: { minimumFractionDigits: 2, maximumFractionDigits: 2 } };
    if (formatter === formatPercent) return { format: { minimumFractionDigits: 2, maximumFractionDigits: 2 }, suffix: "%" };
    return null;
  })();

  return (
    <section className="rounded-xl border border-border p-4">
      <h3 className="font-display text-base font-semibold text-foreground">{title}</h3>
      {subtitle ? <p className="mb-4 mt-0.5 text-xs text-muted-foreground">{subtitle}</p> : null}
      <div className="space-y-3">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Sem dados para o período.</p>}
        {ordenarCampanhasPrimeiro(rows, labelSelector, campanhasPorNome).map((row, index) => {
          const currentValue = Number(row?.[metricKey] || 0);
          const campanha = campanhasPorNome?.get(chaveCampanha(labelSelector(row)));
          // Mesma leitura do dashboard: o destaque e a propria linha do banco, com a cor da marca.
          return (
            <div key={`${title}-${row.groupKey || index}`} className="space-y-1.5">
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  {campanha ? <CampanhaIcone campanha={campanha} /> : null}
                  <span className="truncate font-medium text-foreground">{labelSelector(row)}</span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {flowFormat ? (
                    <NumberFlowValue
                      className="tabular-nums"
                      value={currentValue}
                      format={flowFormat.format}
                      suffix={flowFormat.suffix}
                    />
                  ) : (
                    formatter(currentValue)
                  )}
                </span>
              </div>
              {/* Barra só onde existe meta: nas demais linhas ela insinuava um alvo inexistente. */}
              <CampanhaMetaBar campanha={campanha} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function toIsoDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildEvolutionSeries({ pontos = [], granularity = "DIA", dataInicio, dataFim, metricKey = "producaoBruta" }) {
  const period = sanitizeJoinPeriodo({ dataInicio, dataFim });
  if (!period?.dataInicio || !period?.dataFim) return [];

  const pointMap = new Map();
  (Array.isArray(pontos) ? pontos : []).forEach((point) => {
    const key = String(point?.data || "").slice(0, 10);
    if (!key) return;
    const metrics = normalizeEvolutionPoint(point);
    pointMap.set(key, (pointMap.get(key) || 0) + Number(metrics?.[metricKey] || 0));
  });

  const normalized = [];
  const start = new Date(`${period.dataInicio}T00:00:00`);
  const end = new Date(`${period.dataFim}T00:00:00`);

  if (granularity === "MES") {
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const lastMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cursor <= lastMonth) {
      const iso = toIsoDate(cursor);
      normalized.push({
        data: iso,
        value: Number(pointMap.get(iso) || 0),
        label: cursor.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }),
        fullLabel: cursor.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }),
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return normalized;
  }

  const cursor = new Date(start);
  while (cursor <= end) {
    const iso = toIsoDate(cursor);
    normalized.push({
      data: iso,
      value: Number(pointMap.get(iso) || 0),
      label: cursor.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
      fullLabel: cursor.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }),
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return normalized;
}

function formatEvolutionTick(value, granularity) {
  const iso = String(value || "").slice(0, 10);
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;

  if (granularity === "MES") {
    return date
      .toLocaleDateString("pt-BR", { month: "short", year: "2-digit" })
      .replace(".", "");
  }

  return date.toLocaleDateString("pt-BR", { day: "numeric", month: "short" }).replace(".", "");
}

function formatEvolutionTooltipLabel(value, granularity) {
  const iso = String(value || "").slice(0, 10);
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;

  if (granularity === "MES") {
    return date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  }

  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}

function EvolutionAreaChart({
  pontos = [],
  granularity = "DIA",
  dataInicio,
  dataFim,
  informacoes,
  activeMetric = "producaoBruta",
  onMetricChange,
}) {
  const metric = EVOLUTION_METRIC_OPTIONS.find((item) => item.key === activeMetric) || EVOLUTION_METRIC_OPTIONS[0];
  const normalized = useMemo(
    () => buildEvolutionSeries({ pontos, granularity, dataInicio, dataFim, metricKey: metric.key }),
    [pontos, granularity, dataInicio, dataFim, metric.key]
  );

  const chartConfig = useMemo(
    () => ({
      value: { label: metric.label, color: metric.color },
    }),
    [metric]
  );

  return (
    <div>
      <div className="mb-4 space-y-3">
        <div className="space-y-1">
          <h3 className="font-display text-base font-semibold text-foreground">Evolução da produção</h3>
          <p className="text-xs text-muted-foreground">
            Visualização por {granularity === "MES" ? "mês" : "dia"} da {metric.description} do período atual.
          </p>
          {informacoes ? <p className="text-xs text-muted-foreground">{informacoes}</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {EVOLUTION_METRIC_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onMetricChange?.(option.key)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                option.key === metric.key
                  ? "border-transparent bg-foreground text-background shadow-lg"
                  : "border-border bg-background/50 text-muted-foreground hover:text-foreground"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {normalized.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sem evolução no período selecionado.</p>
      ) : (
        <div className="rounded-3xl border border-border/70 bg-background/40 p-4">
          <ChartContainer config={chartConfig} className="h-[320px] w-full">
            <AreaChart accessibilityLayer data={normalized} margin={{ left: 8, right: 12, top: 18, bottom: 6 }}>
              <defs>
                <linearGradient id={`join-evo-fill-${metric.key}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-value)" stopOpacity="0.38" />
                  <stop offset="100%" stopColor="var(--color-value)" stopOpacity="0.06" />
                </linearGradient>
              </defs>

              <CartesianGrid vertical={false} stroke="hsl(var(--border) / 0.55)" />
              <XAxis
                dataKey="data"
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                minTickGap={22}
                tickMargin={10}
                padding={{ left: 12, right: 12 }}
                tickFormatter={(value) => formatEvolutionTick(value, granularity)}
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={58}
                tickFormatter={(value) => (metric.key === "contratosTotal" ? formatCount(value) : formatCompactBRL(value))}
                stroke="hsl(var(--muted-foreground))"
                tick={{ fontSize: 12 }}
              />

              <ChartTooltip
                cursor={{ stroke: "hsl(var(--border) / 0.6)", strokeWidth: 1 }}
                content={
                  <ChartTooltipContent
                    labelFormatter={(value) => formatEvolutionTooltipLabel(value, granularity)}
                    formatter={(value) => [metric.formatter(value), metric.label]}
                    indicator="dot"
                  />
                }
              />
              <ChartLegend verticalAlign="top" align="left" content={<ChartLegendContent />} wrapperStyle={{ paddingLeft: 52 }} />

              <Area
                type="monotone"
                dataKey="value"
                stroke="var(--color-value)"
                strokeWidth={2.2}
                fill={`url(#join-evo-fill-${metric.key})`}
                isAnimationActive={true}
              />
            </AreaChart>
          </ChartContainer>
        </div>
      )}
    </div>
  );
}

function ContractStatusStackedBarCard({
  pontos = [],
  granularity = "DIA",
  dataInicio,
  dataFim,
  informacoes,
}) {
  const chartData = useMemo(() => {
    const normalizedPoints = buildEvolutionSeries({
      pontos,
      granularity,
      dataInicio,
      dataFim,
      metricKey: "contratosTotal",
    });

    const statusByDate = new Map();
    (Array.isArray(pontos) ? pontos : []).forEach((point) => {
      const key = String(point?.data || "").slice(0, 10);
      if (!key) return;
      const metrics = normalizeContractStatusPoint(point);
      const current = statusByDate.get(key) || { aprovados: 0, cancelados: 0, negados: 0, emAndamento: 0 };
      statusByDate.set(key, {
        aprovados: current.aprovados + Number(metrics.contratosAprovados || 0),
        cancelados: current.cancelados + Number(metrics.contratosCancelados || 0),
        negados: current.negados + Number(metrics.contratosNegados || 0),
        emAndamento: current.emAndamento + Number(metrics.contratosEmAndamento || 0),
      });
    });

    return normalizedPoints.map((point) => {
      const status = statusByDate.get(point.data) || { aprovados: 0, cancelados: 0, negados: 0, emAndamento: 0 };
      return {
        data: point.data,
        aprovados: status.aprovados,
        cancelados: status.cancelados,
        negados: status.negados,
        emAndamento: status.emAndamento,
      };
    });
  }, [pontos, granularity, dataInicio, dataFim]);

  const chartConfig = useMemo(
    () => ({
      // Mesmas cores dos cartões de digitação (utils/kpiCard), para a legenda bater com eles.
      aprovados: { label: "Aprovados", color: "hsl(var(--badge-success))" },
      cancelados: { label: "Cancelados", color: "hsl(var(--badge-danger))" },
      negados: { label: "Negados", color: "hsl(var(--sidebar-primary))" },
      emAndamento: { label: "Em andamento", color: "var(--chart-1)" },
    }),
    []
  );

  const hasMovement = useMemo(
    () =>
      chartData.some(
        (point) =>
          Number(point?.aprovados || 0) +
            Number(point?.cancelados || 0) +
            Number(point?.negados || 0) +
            Number(point?.emAndamento || 0) >
          0
      ),
    [chartData]
  );

  return (
    <div>
      <div className="space-y-4">
        <div className="space-y-1">
          <h3 className="font-display text-base font-semibold text-foreground">Status dos contratos no tempo</h3>
          <p className="text-xs text-muted-foreground">
            Aprovados, cancelados, negados e em andamento em cada data do período.
          </p>
        </div>
        {informacoes ? <p className="text-xs text-muted-foreground">{informacoes}</p> : null}

        {hasMovement ? (
          <div className="rounded-3xl border border-border/70 bg-background/40 p-4">
            <ChartContainer config={chartConfig} className="h-[320px] w-full">
              <BarChart accessibilityLayer data={chartData} margin={{ left: 10, right: 12, top: 18, bottom: 6 }}>
                <CartesianGrid vertical={false} stroke="hsl(var(--border) / 0.55)" />
                <XAxis
                  dataKey="data"
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={22}
                  tickMargin={10}
                  padding={{ left: 12, right: 12 }}
                  tickFormatter={(value) => formatEvolutionTick(value, granularity)}
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={52}
                  tickFormatter={(value) => formatCount(value)}
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 12 }}
                />

                <ChartTooltip
                  cursor={{ fill: "transparent" }}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) => formatEvolutionTooltipLabel(value, granularity)}
                      formatter={(value, _name, entry) => {
                        const payload = entry?.payload || {};
                        const total =
                          Number(payload?.aprovados || 0) +
                          Number(payload?.cancelados || 0) +
                          Number(payload?.negados || 0) +
                          Number(payload?.emAndamento || 0);
                        const numericValue = Number(value || 0);
                        const percent = total > 0 ? (numericValue / total) * 100 : 0;
                        const label = chartConfig?.[entry?.dataKey]?.label || entry?.name;
                        return [`${formatCount(numericValue)} (${percent.toFixed(1).replace(".", ",")}%)`, label];
                      }}
                      indicator="dot"
                    />
                  }
                />
                <ChartLegend verticalAlign="top" align="left" content={<ChartLegendContent />} wrapperStyle={{ paddingLeft: 52 }} />

                <Bar dataKey="aprovados" stackId="statusContratos" fill="var(--color-aprovados)" radius={[6, 6, 0, 0]} maxBarSize={22} isAnimationActive={true} />
                <Bar dataKey="cancelados" stackId="statusContratos" fill="var(--color-cancelados)" radius={[6, 6, 0, 0]} maxBarSize={22} isAnimationActive={true} />
                <Bar dataKey="negados" stackId="statusContratos" fill="var(--color-negados)" radius={[6, 6, 0, 0]} maxBarSize={22} isAnimationActive={true} />
                <Bar dataKey="emAndamento" stackId="statusContratos" fill="var(--color-emAndamento)" radius={[6, 6, 0, 0]} maxBarSize={22} isAnimationActive={true} />
              </BarChart>
            </ChartContainer>
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Sem evolução de status de contratos no período selecionado.
          </div>
        )}
      </div>
    </div>
  );
}

export default function VidaAgenteDetalhada() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const periodoUrl = { dataInicio: searchParams.get("de") || "", dataFim: searchParams.get("ate") || "" };

  const [loadingBase, setLoadingBase] = useState(true);
  const [parceiro, setParceiro] = useState(null);
  const [bancos, setBancos] = useState([]);
  const [convenios, setConvenios] = useState([]);

  const [filters, setFilters] = useState(() => loadFiltersFromSession(id, periodoUrl));
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [preFiltroIds, setPreFiltroIds] = useState([]);
  const [abaAtiva, setAbaAtiva] = useState("evolucao");
  const [refreshCountdown, setRefreshCountdown] = useState(AUTO_REFRESH_SECONDS);
  const requestSeqRef = useRef(0);
  const [payloadAtual, setPayloadAtual] = useState({});
  const [payloadAnterior, setPayloadAnterior] = useState({});
  const [activeEvolutionMetric, setActiveEvolutionMetric] = useState("producaoBruta");
  const [rankingMetricMode, setRankingMetricMode] = useState("BRUTO");
  const evolutionGranularity = "DIA";

  useEffect(() => {
    setFilters(loadFiltersFromSession(id, periodoUrl));
  }, [id]);

  useEffect(() => {
    (async () => {
      setLoadingBase(true);
      try {
        const [parceiroAtual, bancosAtivos, conveniosAtivos] = await Promise.all([
          buscarParceiro(id),
          listarBancosAtivos(),
          listarConveniosAtivos(),
        ]);
        setParceiro(parceiroAtual || null);
        setBancos(Array.isArray(bancosAtivos) ? bancosAtivos : []);
        setConvenios(Array.isArray(conveniosAtivos) ? conveniosAtivos : []);
      } catch {
        SwalWithTheme.error("Erro", "Não foi possível carregar a vida do agente detalhada");
        navigate(`/parceiros/${id}`, { replace: true });
      } finally {
        setLoadingBase(false);
      }
    })();
  }, [id, navigate]);

  useEffect(() => {
    if (!id || typeof window === "undefined") return;
    window.sessionStorage.setItem(getStorageKey(id), JSON.stringify(filters));
  }, [filters, id]);

  const bancosOptions = useMemo(
    () =>
      (bancos || [])
        .map((item) => ({ value: String(item?.bancoId ?? ""), label: String(item?.bancoNome || item?.bancoId || "") }))
        .filter((item) => item.value),
    [bancos]
  );

  const conveniosOptions = useMemo(
    () =>
      (convenios || [])
        .map((item) => ({ value: String(item?.convenioId ?? ""), label: String(item?.convenioNome || item?.convenioId || "") }))
        .filter((item) => item.value),
    [convenios]
  );

  const tiposConvenioOptions = useMemo(() => {
    const map = new Map();
    (convenios || []).forEach((convenio) => {
      (convenio?.tiposConvenio || []).forEach((tipo) => {
        const codigo = String(tipo?.codigo || "").trim().toUpperCase();
        if (!codigo || map.has(codigo)) return;
        map.set(codigo, {
          value: codigo,
          label: String(tipo?.nome || codigo).trim() || codigo,
          order: Number.isFinite(Number(tipo?.ordem))
            ? Number(tipo.ordem)
            : TIPO_PRODUTO_ORDER[codigo] ?? 999,
        });
      });
    });

    const options = Array.from(map.values())
      .sort((left, right) => Number(left.order ?? 999) - Number(right.order ?? 999) || left.label.localeCompare(right.label))
      .map(({ order, ...option }) => option);
    if (options.length) return options;
    return [
      { value: "NOVO", label: "Contrato Novo", order: TIPO_PRODUTO_ORDER.NOVO ?? 0 },
      { value: "PORT", label: "Portabilidade", order: TIPO_PRODUTO_ORDER.PORT ?? 1 },
      { value: "REFIN", label: "Refinanciamento", order: TIPO_PRODUTO_ORDER.REFIN ?? 2 },
    ]
      .sort((left, right) => Number(left.order ?? 999) - Number(right.order ?? 999))
      .map(({ order, ...option }) => option);
  }, [convenios]);

  useEffect(() => {
    const validValues = new Set(tiposConvenioOptions.map((option) => String(option.value)));
    setFilters((prev) => {
      const nextTipos = (prev.tiposConvenio || []).filter((value) => validValues.has(String(value)));
      if (nextTipos.length === (prev.tiposConvenio || []).length) {
        return prev;
      }
      return { ...prev, tiposConvenio: nextTipos };
    });
  }, [tiposConvenioOptions]);

  const carregarDados = async (nextFilters = filters, { silent = false } = {}) => {
    const sanitizedFilters = { ...nextFilters, ...sanitizeJoinPeriodo(nextFilters) };

    if (!parceiro?.idJoin) {
      setPayloadAtual({});
      setPayloadAnterior({});
      return;
    }

    const periodoAnterior = buildJoinPeriodoComparativo(sanitizedFilters);
    // Filtro trocado no meio de uma consulta: só a resposta da última vale.
    const requestSeq = ++requestSeqRef.current;

    setLoading(true);
    try {
      const [atual, anterior] = await Promise.all([
        buscarVidaVendedorDetalhada({
          vendedorId: parceiro.idJoin,
          dataInicio: sanitizedFilters.dataInicio,
          dataFim: sanitizedFilters.dataFim,
          bancosIds: sanitizedFilters.bancosIds.map(Number),
          conveniosIds: sanitizedFilters.conveniosIds.map(Number),
          tiposConvenio: sanitizedFilters.tiposConvenio,
          granularidadeEvolucao: evolutionGranularity,
          tipoPeriodo: "APROVACAO",
        }),
        buscarVidaVendedorDetalhada({
          vendedorId: parceiro.idJoin,
          dataInicio: periodoAnterior.dataInicio,
          dataFim: periodoAnterior.dataFim,
          bancosIds: sanitizedFilters.bancosIds.map(Number),
          conveniosIds: sanitizedFilters.conveniosIds.map(Number),
          tiposConvenio: sanitizedFilters.tiposConvenio,
          granularidadeEvolucao: evolutionGranularity,
          tipoPeriodo: "APROVACAO",
        }),
      ]);
      if (requestSeq !== requestSeqRef.current) return;
      setPayloadAtual(atual || {});
      setPayloadAnterior(anterior || {});
      setFilters((prev) => ({ ...prev, dataInicio: sanitizedFilters.dataInicio, dataFim: sanitizedFilters.dataFim }));
    } catch (error) {
      if (requestSeq !== requestSeqRef.current) return;
      // A Join oscila: na recarga automática, mantém os números atuais em vez de zerar e alertar.
      if (silent) return;
      const message = error?.response?.data?.message || "Não foi possível carregar a vida do agente detalhada";
      SwalWithTheme.warning("JoinConsig", message);
      setPayloadAtual({});
      setPayloadAnterior({});
    } finally {
      if (requestSeq === requestSeqRef.current) setLoading(false);
    }
  };

  // Filtro aplica sozinho (antes exigia clicar em "Atualizar análise"). A espera curta junta mudanças
  // seguidas numa consulta só à Join.
  useEffect(() => {
    if (!parceiro?.idJoin) return undefined;
    const timeout = window.setTimeout(() => carregarDados(), 400);
    return () => window.clearTimeout(timeout);
  }, [
    parceiro?.idJoin,
    filters.dataInicio,
    filters.dataFim,
    filters.bancosIds.join("|"),
    filters.conveniosIds.join("|"),
    filters.tiposConvenio.join("|"),
  ]);

  // Recarga automática com a contagem na barra superior, igual ao dashboard.
  const autoRefreshRef = useRef(null);
  autoRefreshRef.current = ({ manual = false } = {}) => {
    if (!parceiro?.idJoin) return;
    if (!manual && (document.hidden || filtersOpen)) return;
    carregarDados(filters, { silent: true });
  };

  useEffect(() => {
    setRefreshCountdown(AUTO_REFRESH_SECONDS);
    const intervalId = window.setInterval(() => {
      setRefreshCountdown((current) => Math.max(0, current - 1));
    }, 1000);

    const handleRefreshRequest = () => {
      autoRefreshRef.current?.({ manual: true });
      setRefreshCountdown(AUTO_REFRESH_SECONDS);
    };
    window.addEventListener(DASHBOARD_REFRESH_REQUEST_EVENT, handleRefreshRequest);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(DASHBOARD_REFRESH_REQUEST_EVENT, handleRefreshRequest);
      clearDashboardRefreshState();
    };
  }, [id]);

  // Recarga fora do updater do setState: em StrictMode o updater roda duas vezes.
  useEffect(() => {
    if (refreshCountdown > 0) return;
    autoRefreshRef.current?.();
    setRefreshCountdown(AUTO_REFRESH_SECONDS);
  }, [refreshCountdown]);

  useEffect(() => {
    publishDashboardRefreshState({
      visible: true,
      loading,
      countdown: refreshCountdown,
      countdownLabel: formatRefreshCountdown(refreshCountdown),
    });
  }, [loading, refreshCountdown]);

  // Funil da barra superior: período, bancos, convênios e tipo de produto. Período só conta quando
  // difere do padrão (mês útil corrente).
  const periodoPadrao = sanitizeJoinPeriodo();
  const activeFilterCount =
    filters.bancosIds.length +
    filters.conveniosIds.length +
    filters.tiposConvenio.length +
    (filters.dataInicio !== periodoPadrao.dataInicio || filters.dataFim !== periodoPadrao.dataFim ? 1 : 0);

  const handleClearFilters = () => {
    setFilters((prev) => ({ ...prev, ...sanitizeJoinPeriodo(), bancosIds: [], conveniosIds: [], tiposConvenio: [] }));
  };

  usePageFilters({ count: activeFilterCount, onOpen: () => setFiltersOpen(true) });
  // Breadcrumb: Parceiros › Nome (link para os detalhes) › Vida do agente.
  useBreadcrumbEntity(`/parceiros/${id}`, nomeExibicaoParceiro(parceiro?.nome));

  const rowsLeafAtual = useMemo(() => flattenJoinProducaoAgrupada(payloadAtual?.producaoAgrupada), [payloadAtual]);
  const rowsLeafAnterior = useMemo(() => flattenJoinProducaoAgrupada(payloadAnterior?.producaoAgrupada), [payloadAnterior]);

  const grouping = useMemo(
    () => ({ banco: filters.agruparBanco, convenio: filters.agruparConvenio, tipo: filters.agruparTipo }),
    [filters.agruparBanco, filters.agruparConvenio, filters.agruparTipo]
  );

  const rowsAgrupadasAtual = useMemo(() => aggregateJoinRows(rowsLeafAtual, grouping), [rowsLeafAtual, grouping]);
  const rowsAgrupadasAnterior = useMemo(() => aggregateJoinRows(rowsLeafAnterior, grouping), [rowsLeafAnterior, grouping]);
  const rowsAgrupadasAnterioresMap = useMemo(() => buildJoinComparisonMap(rowsAgrupadasAnterior), [rowsAgrupadasAnterior]);
  const rowsTabela = useMemo(() => filterJoinRowsBySearch(rowsAgrupadasAtual, filters.searchTerm), [rowsAgrupadasAtual, filters.searchTerm]);

  const resumoAtual = payloadAtual?.resumoPeriodo || {};
  const resumoAnterior = payloadAnterior?.resumoPeriodo || {};
  const contratosAtual = useMemo(() => sumJoinContracts(rowsLeafAtual), [rowsLeafAtual]);
  const contratosAnterior = useMemo(() => sumJoinContracts(rowsLeafAnterior), [rowsLeafAnterior]);

  const periodoAtualDescricao = useMemo(() => formatPeriodoDescricao(filters), [filters]);
  const periodoAnteriorDescricao = useMemo(() => formatPeriodoDescricao(buildJoinPeriodoComparativo(filters)), [filters]);

  const producaoCards = useMemo(() => {
    const items = [
      {
        label: "Produção bruta total",
        valueLabel: formatCurrency(resumoAtual?.producaoBrutaTotal),
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(resumoAtual?.producaoBrutaTotal || 0)}
            format={{ style: "currency", currency: "BRL" }}
          />
        ),
        previousValue: formatCurrency(resumoAnterior?.producaoBrutaTotal),
        icon: Wallet,
        change: calculateChange(resumoAtual?.producaoBrutaTotal, resumoAnterior?.producaoBrutaTotal),
      },
      {
        label: "Produção líquida total",
        valueLabel: formatCurrency(resumoAtual?.producaoLiquidaTotal),
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(resumoAtual?.producaoLiquidaTotal || 0)}
            format={{ style: "currency", currency: "BRL" }}
          />
        ),
        previousValue: formatCurrency(resumoAnterior?.producaoLiquidaTotal),
        icon: TrendingUp,
        change: calculateChange(resumoAtual?.producaoLiquidaTotal, resumoAnterior?.producaoLiquidaTotal),
      },
      {
        label: "Produção comissionada total",
        valueLabel: formatCurrency(resumoAtual?.producaoComissionadaTotal),
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(resumoAtual?.producaoComissionadaTotal || 0)}
            format={{ style: "currency", currency: "BRL" }}
          />
        ),
        previousValue: formatCurrency(resumoAnterior?.producaoComissionadaTotal),
        icon: BarChart3,
        change: calculateChange(resumoAtual?.producaoComissionadaTotal, resumoAnterior?.producaoComissionadaTotal),
      },
      {
        label: "Repasse médio",
        valueLabel: formatPercent(resumoAtual?.percentualRepasseMedio),
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(resumoAtual?.percentualRepasseMedio || 0)}
            format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
            suffix="%"
          />
        ),
        previousValue: formatPercent(resumoAnterior?.percentualRepasseMedio),
        icon: Percent,
        change: calculateChange(resumoAtual?.percentualRepasseMedio, resumoAnterior?.percentualRepasseMedio),
        showComparison: false,
      },
      {
        label: "Spread médio",
        valueLabel: formatSpread(resumoAtual?.spreadMedio),
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(resumoAtual?.spreadMedio || 0)}
            format={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
          />
        ),
        previousValue: formatSpread(resumoAnterior?.spreadMedio),
        icon: LineChart,
        change: calculateChange(resumoAtual?.spreadMedio, resumoAnterior?.spreadMedio),
        showComparison: false,
      },
    ];

    return items.map((item) => ({
      ...item,
      tooltip: buildComparisonTitle(item.valueLabel ?? item.value, item.previousValue),
    }));
  }, [resumoAnterior, resumoAtual]);

  const contratosCards = useMemo(() => {
    const items = [
      {
        label: "Total digitado",
        valueLabel: formatCount(contratosAtual.contratosTotal),
        value: (
          <NumberFlowValue className="tabular-nums" value={Number(contratosAtual.contratosTotal || 0)} format={{ maximumFractionDigits: 0 }} />
        ),
        previousValue: formatCount(contratosAnterior.contratosTotal),
        icon: FileText,
        change: calculateChange(contratosAtual.contratosTotal, contratosAnterior.contratosTotal),
      },
      {
        label: "Aprovados",
        valueLabel: formatCount(contratosAtual.contratosAprovados),
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(contratosAtual.contratosAprovados || 0)}
            format={{ maximumFractionDigits: 0 }}
          />
        ),
        previousValue: formatCount(contratosAnterior.contratosAprovados),
        icon: CheckCircle2,
        change: calculateChange(contratosAtual.contratosAprovados, contratosAnterior.contratosAprovados),
      },
      {
        label: "Cancelados",
        valueLabel: formatCount(contratosAtual.contratosCancelados),
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(contratosAtual.contratosCancelados || 0)}
            format={{ maximumFractionDigits: 0 }}
          />
        ),
        previousValue: formatCount(contratosAnterior.contratosCancelados),
        icon: XCircle,
        change: calculateChange(contratosAtual.contratosCancelados, contratosAnterior.contratosCancelados),
        lowerIsBetter: true,
      },
      {
        label: "Negados",
        valueLabel: formatCount(contratosAtual.contratosNegados),
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(contratosAtual.contratosNegados || 0)}
            format={{ maximumFractionDigits: 0 }}
          />
        ),
        previousValue: formatCount(contratosAnterior.contratosNegados),
        icon: Ban,
        change: calculateChange(contratosAtual.contratosNegados, contratosAnterior.contratosNegados),
        lowerIsBetter: true,
      },
      {
        label: "Em andamento",
        valueLabel: formatCount(contratosAtual.contratosEmAndamento),
        value: (
          <NumberFlowValue
            className="tabular-nums"
            value={Number(contratosAtual.contratosEmAndamento || 0)}
            format={{ maximumFractionDigits: 0 }}
          />
        ),
        previousValue: formatCount(contratosAnterior.contratosEmAndamento),
        icon: Clock3,
        change: calculateChange(contratosAtual.contratosEmAndamento, contratosAnterior.contratosEmAndamento),
        showComparison: false,
      },
    ];

    return items.map((item) => ({
      ...item,
      tooltip: buildComparisonTitle(item.valueLabel ?? item.value, item.previousValue),
    }));
  }, [contratosAnterior, contratosAtual]);

  const rankingMeta = useMemo(
    () => RANKING_METRIC_OPTIONS.find((option) => option.key === rankingMetricMode) || RANKING_METRIC_OPTIONS[0],
    [rankingMetricMode]
  );
  const [campanhasVigentes, setCampanhasVigentes] = useState([]);
  useEffect(() => {
    let ativo = true;
    listarCampanhasVigentes()
      .then((lista) => {
        if (ativo) setCampanhasVigentes(lista);
      })
      .catch(() => {
        if (ativo) setCampanhasVigentes([]);
      });
    return () => {
      ativo = false;
    };
  }, []);
  // Aqui o ranking traz o nome do banco/convenio na propria linha, entao o nome da campanha sai da
  // lista de opcoes dos filtros, que ja esta carregada.
  const campanhasPorNomeBanco = useMemo(() => {
    const mapa = new Map();
    campanhasVigentes.filter((campanha) => campanha.tipo === "BANCO").forEach((campanha) => {
      const nome = bancosOptions.find((opcao) => String(opcao.value) === String(campanha.referenciaId))?.label;
      // nomeReferencia serve para a linha fixada quando o banco nao aparece no ranking.
      if (nome) mapa.set(chaveCampanha(nome), { ...campanha, nomeReferencia: nome });
    });
    return mapa;
  }, [campanhasVigentes, bancosOptions]);
  const campanhasPorNomeConvenio = useMemo(() => {
    const mapa = new Map();
    campanhasVigentes.filter((campanha) => campanha.tipo === "CONVENIO").forEach((campanha) => {
      const nome = conveniosOptions.find((opcao) => String(opcao.value) === String(campanha.referenciaId))?.label;
      // nomeReferencia serve para a linha fixada quando o banco nao aparece no ranking.
      if (nome) mapa.set(chaveCampanha(nome), { ...campanha, nomeReferencia: nome });
    });
    return mapa;
  }, [campanhasVigentes, conveniosOptions]);

  const bancosRanking = useMemo(() => {
    const items = aggregateJoinRows(rowsLeafAtual, { banco: true, convenio: false, tipo: false });
    return items
      .slice()
      .sort((a, b) => Number(b?.[rankingMeta.metricKey] || 0) - Number(a?.[rankingMeta.metricKey] || 0))
      .slice(0, 5);
  }, [rowsLeafAtual, rankingMeta.metricKey]);
  const conveniosRanking = useMemo(() => {
    const items = aggregateJoinRows(rowsLeafAtual, { banco: false, convenio: true, tipo: false });
    return items
      .slice()
      .sort((a, b) => Number(b?.[rankingMeta.metricKey] || 0) - Number(a?.[rankingMeta.metricKey] || 0))
      .slice(0, 6);
  }, [rowsLeafAtual, rankingMeta.metricKey]);
  const tiposRanking = useMemo(() => {
    const items = aggregateJoinRows(rowsLeafAtual, { banco: false, convenio: false, tipo: true });
    return items
      .slice()
      .sort((a, b) => Number(b?.[rankingMeta.metricKey] || 0) - Number(a?.[rankingMeta.metricKey] || 0))
      .slice(0, 6);
  }, [rowsLeafAtual, rankingMeta.metricKey]);

  const tabelaColumns = useMemo(() => {
    const columns = [];
    if (grouping.banco) columns.push({ key: "bancoNome", header: "Banco", minWidth: 180, mobilePrimary: true, mobileOrder: 0 });
    if (grouping.convenio) columns.push({ key: "convenioNome", header: "Convênio", minWidth: 160, mobileSpan: 2, mobileOrder: 1 });
    if (grouping.tipo) columns.push({ key: "tipoConvenio", header: TIPO_PRODUTO_LABEL, minWidth: 160, mobileSpan: 1, mobileOrder: 2 });

    return [
      ...columns,
      { key: "producaoBrutaTotal", header: "Prod. bruta", minWidth: 170, mobileSpan: 1, mobileOrder: 10, render: (_, row) => <MetricCell value={row.producaoBrutaTotal} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.producaoBrutaTotal} formatter={formatCurrency} /> },
      { key: "producaoLiquidaTotal", header: "Prod. líquida", minWidth: 170, mobileSpan: 1, mobileOrder: 11, render: (_, row) => <MetricCell value={row.producaoLiquidaTotal} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.producaoLiquidaTotal} formatter={formatCurrency} /> },
      { key: "producaoComissionadaTotal", header: "Prod. comissionada", minWidth: 190, mobileSpan: 1, mobileOrder: 12, render: (_, row) => <MetricCell value={row.producaoComissionadaTotal} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.producaoComissionadaTotal} formatter={formatCurrency} /> },
      { key: "percentualRepasse", header: "Repasse", minWidth: 150, mobileSpan: 1, mobileOrder: 13, render: (_, row) => <MetricCell value={row.percentualRepasse} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.percentualRepasse} formatter={formatPercent} showComparison={false} /> },
      { key: "spread", header: "Spread", minWidth: 140, mobileSpan: 1, mobileOrder: 14, render: (_, row) => <MetricCell value={row.spread} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.spread} formatter={formatSpread} showComparison={false} /> },
      { key: "contratosTotal", header: "Contratos total", minWidth: 145, mobileSpan: 1, mobileOrder: 15, render: (_, row) => <MetricCell value={row.contratosTotal} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.contratosTotal} formatter={formatCount} /> },
      { key: "contratosAprovados", header: "Aprovados", minWidth: 130, mobileSpan: 1, mobileOrder: 16, render: (_, row) => <MetricCell value={row.contratosAprovados} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.contratosAprovados} formatter={formatCount} /> },
      { key: "contratosNegados", header: "Negados", minWidth: 120, mobileSpan: 1, mobileOrder: 17, render: (_, row) => <MetricCell value={row.contratosNegados} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.contratosNegados} formatter={formatCount} lowerIsBetter /> },
      { key: "contratosCancelados", header: "Cancelados", minWidth: 140, mobileSpan: 1, mobileOrder: 18, render: (_, row) => <MetricCell value={row.contratosCancelados} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.contratosCancelados} formatter={formatCount} lowerIsBetter /> },
      { key: "contratosEmAndamento", header: "Andamento", minWidth: 140, mobileSpan: 1, mobileOrder: 19, render: (_, row) => <MetricCell value={row.contratosEmAndamento} previousValue={rowsAgrupadasAnterioresMap.get(row.groupKey)?.contratosEmAndamento} formatter={formatCount} showComparison={false} /> },
    ];
  }, [grouping.banco, grouping.convenio, grouping.tipo, rowsAgrupadasAnterioresMap]);

  // Pelo menos um agrupamento fica marcado: sem nenhum, a lista viraria uma linha só sem rótulo.
  const toggleAgrupamento = (campo, checked) => {
    setFilters((prev) => {
      const next = { ...prev, [campo]: Boolean(checked) };
      if (!next.agruparBanco && !next.agruparConvenio && !next.agruparTipo) return prev;
      return next;
    });
  };

  if (loadingBase) {
    return <PageLoader label="Carregando vida do agente..." />;
  }

  // Carregando de tela cheia só na primeira carga: nas seguintes (filtro, recarga automática) os
  // números ficam na tela e o ícone da barra superior gira.
  const carregandoPrimeiraVez = loading && !Object.keys(payloadAtual || {}).length;
  const rankingSubtitle = `Por ${rankingMeta.label.toLowerCase()} no período atual.`;

  return (
    <div className="space-y-5">
      <ParceiroIdentidade parceiro={parceiro} onNomeClick={() => navigate(`/parceiros/${id}`)} />

      {!parceiro?.idJoin ? (
        <div className={`rounded-xl p-4 text-sm ${toneClassMap.warningPanel}`}>
          Defina o ID Join do parceiro para habilitar a vida do agente detalhada.
        </div>
      ) : null}

      {carregandoPrimeiraVez ? (
        <div className="flex min-h-[360px] items-center justify-center rounded-xl border border-border bg-card">
          <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span>Carregando análise detalhada...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Produção fixa acima das abas: é o que mais se consulta. */}
          <Card title="Produção">
            <p className="text-xs text-muted-foreground">
              {periodoAtualDescricao}, comparado com {periodoAnteriorDescricao}. Período, bancos, convênios e tipo de
              produto no filtro da barra superior.
            </p>
            {resumoAtual?.informacoes ? <p className="mt-1 text-xs text-muted-foreground">{resumoAtual.informacoes}</p> : null}
            <KpiCardGrid cards={producaoCards} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5" />
          </Card>

          {/* Análises em abas, como nos detalhes do parceiro: troca de aba em vez de rolar a página. */}
          <Card>
            <Tabs
              items={VIDA_ABAS}
              value={abaAtiva}
              onChange={setAbaAtiva}
              idPrefix={VIDA_ABAS_ID}
              ariaLabel="Análises da vida do agente"
              className="mb-5"
            />

            {abaAtiva === "evolucao" && (
              <div {...tabPanelProps(VIDA_ABAS_ID, "evolucao")} className="motion-safe:animate-tab-in">
                <EvolutionAreaChart
                  pontos={payloadAtual?.evolucaoNoTempo?.pontos}
                  granularity={evolutionGranularity}
                  dataInicio={filters.dataInicio}
                  dataFim={filters.dataFim}
                  informacoes={payloadAtual?.evolucaoNoTempo?.informacoes}
                  activeMetric={activeEvolutionMetric}
                  onMetricChange={setActiveEvolutionMetric}
                />
              </div>
            )}

            {abaAtiva === "digitacao" && (
              <div {...tabPanelProps(VIDA_ABAS_ID, "digitacao")} className="motion-safe:animate-tab-in space-y-6">
                <KpiCardGrid cards={contratosCards} className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5" />
                <ContractStatusStackedBarCard
                  pontos={payloadAtual?.evolucaoNoTempo?.pontos}
                  granularity={evolutionGranularity}
                  dataInicio={filters.dataInicio}
                  dataFim={filters.dataFim}
                />
              </div>
            )}

            {abaAtiva === "rankings" && (
              <div {...tabPanelProps(VIDA_ABAS_ID, "rankings")} className="motion-safe:animate-tab-in space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Métrica</span>
                  {RANKING_METRIC_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setRankingMetricMode(option.key)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                        option.key === rankingMetricMode
                          ? "border-transparent bg-foreground text-background shadow-lg"
                          : "border-border bg-background/50 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  <HorizontalRankingChart
                    title="Top bancos"
                    subtitle={rankingSubtitle}
                    rows={bancosRanking}
                    metricKey={rankingMeta.metricKey}
                    formatter={rankingMeta.formatter}
                    labelSelector={(row) => row.bancoNome}
                    campanhasPorNome={campanhasPorNomeBanco}
                  />
                  <HorizontalRankingChart
                    title="Top convênios"
                    subtitle={rankingSubtitle}
                    rows={conveniosRanking}
                    metricKey={rankingMeta.metricKey}
                    formatter={rankingMeta.formatter}
                    labelSelector={(row) => row.convenioNome}
                    campanhasPorNome={campanhasPorNomeConvenio}
                  />
                  <HorizontalRankingChart
                    title="Tipos de produto"
                    subtitle={rankingSubtitle}
                    rows={tiposRanking}
                    metricKey={rankingMeta.metricKey}
                    formatter={rankingMeta.formatter}
                    labelSelector={(row) => row.tipoConvenio}
                  />
                </div>
              </div>
            )}

            {abaAtiva === "lista" && (
              <div {...tabPanelProps(VIDA_ABAS_ID, "lista")} className="motion-safe:animate-tab-in">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Agrupar por</span>
                    {AGRUPAMENTOS.map(({ campo, label }) => (
                      <label key={campo} className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground">
                        <Checkbox checked={filters[campo]} onCheckedChange={(checked) => toggleAgrupamento(campo, checked)} />
                        {label}
                      </label>
                    ))}
                  </div>
                  <div className="w-full lg:max-w-md">
                    <SearchBar
                      placeholder="Buscar banco, convênio ou tipo de produto..."
                      value={filters.searchTerm}
                      onChange={(event) => setFilters((prev) => ({ ...prev, searchTerm: event.target.value }))}
                    />
                  </div>
                </div>
                <DataTable
                  columns={tabelaColumns}
                  data={rowsTabela}
                  enablePagination={false}
                  rowKey={(row) => row.groupKey}
                  emptyState="Nenhuma linha encontrada para os filtros selecionados"
                />
              </div>
            )}
          </Card>
        </>
      )}

      <FiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Aplicados à produção, gráficos, rankings e lista."
        onClear={handleClearFilters}
        clearDisabled={activeFilterCount === 0}
      >
        <div className="space-y-3">
          <PreFiltroField
            value={preFiltroIds}
            onChange={setPreFiltroIds}
            onApply={(vetores) => setFilters((prev) => ({ ...prev, ...vetores }))}
          />
          <DateRangeField
            label="Período analisado"
            from={filters.dataInicio}
            to={filters.dataFim}
            onFromChange={(event) => setFilters((prev) => ({ ...prev, dataInicio: event.target.value }))}
            onToChange={(event) => setFilters((prev) => ({ ...prev, dataFim: event.target.value }))}
            maxDate={sanitizeJoinPeriodo().dataFim}
          />
          <MultiSelectField
            label="Bancos"
            options={bancosOptions}
            selectedValues={filters.bancosIds}
            onChange={(values) => setFilters((prev) => ({ ...prev, bancosIds: values.map(String) }))}
            placeholder="Todos os bancos"
            searchPlaceholder="Buscar banco..."
            emptyText="Nenhum banco disponível"
          />
          <MultiSelectField
            label="Convênios"
            options={conveniosOptions}
            selectedValues={filters.conveniosIds}
            onChange={(values) => setFilters((prev) => ({ ...prev, conveniosIds: values.map(String) }))}
            placeholder="Todos os convênios"
            searchPlaceholder="Buscar convênio..."
            emptyText="Nenhum convênio disponível"
          />
          <MultiSelectField
            label={TIPO_PRODUTO_LABEL}
            options={tiposConvenioOptions}
            selectedValues={filters.tiposConvenio}
            onChange={(values) => setFilters((prev) => ({ ...prev, tiposConvenio: values.map(String) }))}
            placeholder="Todos os tipos de produto"
            searchPlaceholder="Buscar tipo de produto..."
            emptyText="Nenhum tipo de produto disponível"
          />
        </div>
      </FiltersSheet>
    </div>
  );
}
